/**
 * The value band's geometry — everything that turns a year and an amount into a
 * position in the plot (SPEC-004 PR-4.6).
 *
 * Split out of `CarValueChart.js` when that file passed the repo's 350-line
 * ceiling, and split at the seam that was already there: the component below is
 * markup and copy, and every line here is arithmetic with no opinion about what
 * it draws. It is the same division `accelerationTrace.js` makes for the band
 * above, one layer up — that module owns the arithmetic of the DATA, this one
 * owns the arithmetic of the PICTURE, and neither needs React.
 */

/**
 * The drawing box, in viewBox units.
 *
 * The plot's left inset is wider than the acceleration band's: these tick labels
 * are money rather than three digits of km/h, and `$1.5M` needs the room. The
 * rest matches, so the two charts stack without one looking indented.
 */
const VIEW = { width: 320, height: 180 };

/**
 * The same plot drawn on a bigger sheet of paper, for the reason
 * `accelChartGeometry.js` states at length: a `viewBox` SCALES everything in
 * it, so the phone's frame stretched across a desktop column magnifies the
 * 9-unit money labels and the gold benchmark's dashes along with the series.
 * The result is not a bigger chart, it is the same chart seen too close.
 *
 * Doubling the units and adding a third to the height halves the relative
 * weight of every fixed length and buys the year axis real room — which this
 * series wants more than the acceleration one does, because it runs across
 * forty years and had two labelled points at either end of a crowded plot.
 *
 * Deliberately the SAME frame the acceleration band uses. The two plots now sit
 * one directly above the other in the same column, and two charts a few units
 * apart in weight read as a drawing mistake rather than as a pair.
 */
const WIDE_VIEW = { width: 660, height: 240 };
// `top` seats a LABEL above the highest mark rather than just the mark. The
// acceleration band learned this the hard way: at a ten-unit inset the figure
// above a peak that touches the ceiling has its ascender outside the viewBox,
// and the number arrives with its digits shaved off.
const PLOT = { left: 40, right: 8, top: 20, bottom: 22 };

/**
 * The wide frame's gutters, and they are NOT the narrow ones scaled: the things
 * the insets exist to seat — `$1.5M`, a label above the highest mark — did not
 * get bigger when the frame did. Scaled, they would reserve twice the room for
 * the same numerals. The left one keeps its extra width over the acceleration
 * band's for the reason stated above: money is wider than three digits of km/h.
 */
const WIDE_PLOT = { left: 60, right: 16, top: 22, bottom: 28 };

/**
 * The frame this chart is drawn in, as one object.
 *
 * A function rather than four module constants, because there are two frames
 * now and a module-level pair would silently be the phone's on both. Everything
 * downstream — the projector, the clip rectangle, the axis path — takes its
 * numbers from here, so picking a frame and drawing another is not expressible.
 */
function valueFrame(wide = false) {
    const view = wide ? WIDE_VIEW : VIEW;
    const plot = wide ? WIDE_PLOT : PLOT;

    return {
        view,
        plot,
        plotWidth: view.width - plot.left - plot.right,
        plotHeight: view.height - plot.top - plot.bottom,
    };
}

/**
 * How far above the highest figure the axis runs.
 *
 * The same reasoning as the acceleration band's: scaled exactly to the data, the
 * peak lands ON the frame and its mark is drawn half outside the plot, so the
 * one point that says how high this car ever got is the one that reads as
 * clipped. The year axis needs no equivalent — the plot's own right inset is
 * wider than a mark's radius, so the final point seats itself.
 */
const HEADROOM = 1.08;

/** Data space to viewBox space. The year axis is linear in years, not in points. */
function projector({ from, to, ceiling }, frame = valueFrame()) {
    const span = to - from;
    const top = ceiling * HEADROOM;
    const { plot, plotWidth, plotHeight } = frame;

    return {
        x: (year) => plot.left + ((year - from) / span) * plotWidth,
        y: (amount) => plot.top + plotHeight - (amount / top) * plotHeight,
    };
}

/**
 * The figures printed on the marks, in viewBox units.
 *
 * There is no crowding rule here and there does not need to be one. Only the two
 * SOURCED points are labelled, and they are the first and last points in the
 * series by construction — a launch year and a market reading thirty-odd years
 * apart, at opposite ends of a plot that is two hundred and seventy units wide.
 * They cannot collide, so the acceleration band's displacement arithmetic would
 * be machinery guarding a case that cannot arise.
 */
const LABEL = { offset: 6, height: 9 };

/**
 * The series as one `d`.
 *
 * Straight segments between the points, and unlike the acceleration band there
 * is no smoothing under them. A market is observed, not sampled: a curve through
 * a launch price and four estimates would draw inflections in years nobody has
 * a figure for, and it would draw them in the same ink as the years somebody
 * does. A straight run between two points claims only that it went from here to
 * there, which is the most this series knows.
 */
function seriesPath(points, project) {
    return points
        .map((point, index) => `${index === 0 ? 'M' : 'L'}${project.x(point.year).toFixed(2)} ${project.y(point.amount).toFixed(2)}`)
        .join(' ');
}

/**
 * The window the gold reference is allowed to draw in.
 *
 * A constant id rather than one generated per render: only one value band exists
 * on a card, and two of them would define the same rectangle under the same
 * name — which clips identically rather than wrongly. A generated id would be a
 * new attribute value on every render for no behaviour.
 */
const PLOT_CLIP = 'value-plot-clip';

export { HEADROOM, LABEL, PLOT_CLIP, projector, seriesPath, valueFrame };
